/**
 * The item context menu's entries for a target set of workspace items, on the
 * action bar's implementations (`useActionBarHandlers`): one implementation
 * per action, whichever surface starts it. Enabled state and reasons come
 * from `resolveSelectionActions`, with the action bar's words, so the two
 * surfaces always agree.
 *
 * Menu-only actions: Open in new tab (Open's rule), Copy link (every
 * target's link, one per line, so it never needs disabling), the Add submenu,
 * which exists only for one folder the user can edit (hidden otherwise, like
 * the tree hides it for a viewer), and Manage
 * access for one target (`ManageAccessMenuItem` owns its gating; a
 * multi-selection has no access item, access is set one item at a time).
 *
 * `dialogs` holds every dialog an entry opens; render it beside the menu,
 * not inside it, so a dialog outlives the menu that started it.
 */

import { useState } from 'react';
import { resolveSelectionActions } from '@bower/shared/utils/resolveSelectionActions';
import type { FileItem, SelectionActionId, WorkspaceItem } from '@bower/shared';

import { ROUTES } from '../../../constants/routes';
import { trackLinkOpened } from '../../../hooks/entity/openLinkArtifact';
import { useIsAclEnforced } from '../../../hooks/data/useEntityAccess';
import { useAppToast } from '../../../hooks/ui/useAppToast';
import type { ItemSurface } from '../../../stores/selectionStore';
import { FOLDER_WORD } from '../../../theme/entityIcons';
import { toAclEntityType } from '../../../utils/aclEntityType';
import { getArtifactUrl } from '../../../utils/artifactCapabilities';
import { copyTextToClipboard } from '../../../utils/clipboard';
import { ShareEntityDialog } from '../../dialogs/ShareEntityDialog';
import { EditLinkDialog } from '../../features/links/EditLinkDialog';
import { TreeNodeCreateSubmenu } from '../../navigation/TreeNodeCreateSubmenu';
import { ManageAccessMenuItem } from '../../ui/ManageAccessMenuItem';
import { useActionBarHandlers } from '../action-bar/useActionBarHandlers';
import type { ItemMenuActionId, ItemMenuEntry } from './itemMenuEntries';

function itemUrl(item: WorkspaceItem): string {
  return item.kind === 'folder'
    ? window.location.origin + ROUTES.collection(item.id)
    : getArtifactUrl(item.artifactType, item.id);
}

export function useItemMenuActions(
  targets: WorkspaceItem[],
  renameSurface: ItemSurface | null,
  /** After Delete moved items to the trash (the top bar leaves the trashed folder). */
  onTrashed?: (trashed: readonly WorkspaceItem[]) => void,
) {
  const toast = useAppToast();
  const aclEnforced = useIsAclEnforced();
  const { run, dialogs, isFavorite } = useActionBarHandlers(targets, renameSurface, onTrashed);
  /** The item the share dialog is open for, kept so it outlives the menu. */
  const [accessFor, setAccessFor] = useState<WorkspaceItem | null>(null);
  /** The link the edit dialog is open for, kept so it outlives the menu. */
  const [editingLink, setEditingLink] = useState<FileItem | null>(null);
  const states = resolveSelectionActions(targets, {
    folders: FOLDER_WORD,
  });
  const stateOf = (id: SelectionActionId) => {
    const state = states.find((s) => s.id === id);
    return { enabled: state?.enabled ?? false, reason: state?.reason };
  };
  /** The actions both surfaces offer (no Merge or Download in the menu). */
  const shared = (id: Extract<SelectionActionId, ItemMenuActionId>): ItemMenuEntry => ({
    id,
    ...stateOf(id),
    onSelect: run[id],
  });

  const [only] = targets;
  const editableFolder =
    targets.length === 1 && only?.kind === 'folder' && only.canEdit ? only : null;
  // Only a link row has a URL to edit, so every other row's menu is unchanged.
  const editableLink =
    targets.length === 1 && only?.kind === 'file' && only.link && only.canEdit ? only : null;
  const copyLinks = async () => {
    const copied = await copyTextToClipboard(targets.map(itemUrl).join('\n'));
    toast(
      copied
        ? { title: targets.length === 1 ? 'Link copied' : 'Links copied', status: 'success' }
        : { title: 'Could not copy link', status: 'error' },
    );
  };

  const entries: ItemMenuEntry[] = [
    shared('open'),
    {
      id: 'openInNewTab',
      ...stateOf('open'),
      onSelect: () => {
        if (!only) return;
        window.open(itemUrl(only), '_blank', 'noopener');
        if (only.kind === 'file' && only.artifactType === 'link') trackLinkOpened('item_menu');
      },
    },
    shared('rename'),
    ...(editableLink
      ? [{ id: 'editLink' as const, onSelect: () => setEditingLink(editableLink) }]
      : []),
    shared('move'),
    shared('copy'),
    { id: 'copyLink', onSelect: () => void copyLinks() },
    shared('favorite'),
    shared('delete'),
  ];

  return {
    entries,
    isFavorite,
    /** The "Create in…" item, for one folder the user can edit. */
    addSubmenu: editableFolder ? (
      <TreeNodeCreateSubmenu
        node={{
          id: editableFolder.id,
          name: editableFolder.name,
          type: 'project',
          hasChildren: editableFolder.childCount > 0,
          childCount: editableFolder.childCount,
        }}
        surface="workspace_context_menu"
        onCollectionCreated={stayHere}
      />
    ) : null,
    accessItem:
      targets.length === 1 && only ? (
        <ManageAccessMenuItem
          entityType={toAclEntityType(only)}
          entityId={only.id}
          onOpenDialog={() => setAccessFor(only)}
        />
      ) : null,
    dialogs: (
      <>
        {dialogs}
        {editingLink?.link && (
          <EditLinkDialog
            linkId={editingLink.id}
            initial={{
              url: editingLink.link.url,
              title: editingLink.name,
              favicon: editingLink.link.favicon,
            }}
            onClose={() => setEditingLink(null)}
          />
        )}
        {accessFor && aclEnforced && (
          <ShareEntityDialog
            isOpen
            onClose={() => setAccessFor(null)}
            entityType={toAclEntityType(accessFor)}
            entityId={accessFor.id}
            entityName={accessFor.name}
          />
        )}
      </>
    ),
  };
}

/** A collection made from an item's menu keeps you on the page you are on. */
function stayHere(): void {}
